import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer, getError } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { setDefaultCountryField, isPostalCode } from '../../../../helpers/countriesHelpers';
import { isBench, isMaof } from '../../../../settings';
import { t } from '../../../../i18n';

const initState = {
  deleted: false,
  editForm: null,
  filteredUnits: [],
  formInfo: { countries: [] },
  isEdit: false,
  loading: false,
  selectedUnit: null,
  subUnit: null,
};

const initialSubUnitData = {
  account_manager: null,
  account_size: 'unknown',
  activation_rep: null,
  active: 'false',
  address: '',
  backups_enabled: true,
  breaks_compensation: false,
  breaks_duration: 0,
  breaks_enabled: false,
  city: '',
  clocking_type: 'geoposition',
  company_image_url: '',
  company_logo_url: '',
  cost_center: null,
  country: '',
  industry: '',
  industry_id: '',
  legal_entity_id: '',
  name: '',
  past_jobs_count: '',
  rating: '',
  sales_rep: null,
  sector: '',
  sector_id: '',
  state: '',
  street: '',
  parent_id: null,
  zip: '',
  booked_tier_pay_rate_enabled: null,
};

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const { ...restProps } = data;

  const subUnit = {
    ...restProps,
    address: '',
    industry_id: data.industry ? data.industry.id : '',
    sector_id: data.sector ? data.sector.id : '',
  };

  return {
    ...state,
    subUnit,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function validate(attr, value) {
  let error = '';

  if (attr === 'name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.name_label') });
    }
  }

  if (attr === 'cost_center' && isBench()) {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.cost_center_label') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.country_label') });
    }
  }

  if (attr === 'street') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.street_label') });
    }
  }

  if (attr === 'city') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.city_label') });
    }
  }

  if (attr === 'state' && !isMaof()) {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.state_label') });
    }
  }

  if (attr === 'zip' && !isMaof()) {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.common.location.postal_code') });
    } else if (!isPostalCode(value)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.common.location.postal_code'),
      });
    }
  }

  if (attr === 'sector_id') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.sector_label') });
    }
  }

  if (attr === 'parent_id' && !value) {
    error = t('dash.validation.required_error', { fieldName: t('dash.subUnits.subUnit.unit_label') });
  }

  return error;
}

function onEdit(state, { payload }) {
  const { subUnit, selectedUnit } = payload;

  const propsArray = Object.keys(subUnit);

  const getFormValue = prop => {
    if (prop === 'state') {
      return subUnit[prop]?.id;
    }

    return subUnit[prop] && Object.prototype.hasOwnProperty.call(subUnit[prop], 'id')
      ? subUnit[prop].id
      : subUnit[prop];
  };

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: getFormValue(prop),
      error: prop === 'breaks_duration' && !subUnit.breaks_enabled ? '' : validate(prop, subUnit[prop]),
      touched: false,
    }),
  );

  if (editForm.active) {
    editForm.active.value = editForm.active.value ? 'true' : 'false';
  }

  return {
    ...state,
    isEdit: true,
    editForm,
    selectedUnit,
    filteredUnits: selectedUnit ? [selectedUnit] : [],
  };
}

function onSaveSuccess(state) {
  return {
    ...state,
    subUnit: null,
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    deleted: true,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialSubUnitData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialSubUnitData[prop],
      error: validate(prop, initialSubUnitData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    subUnit: initialSubUnitData,
    editForm: {
      ...editForm,
      country: setDefaultCountryField(),
      active: {
        ...editForm.active,
        value: state.formInfo.activation_statuses[0].id,
      },
      booked_tier_pay_rate_enabled: {
        ...editForm.booked_tier_pay_rate_enabled,
        value: state?.formInfo?.default_booked_tier_pay_rate_enabled || false,
      },
    },
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormDataSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      ...data,
    },
  };
}

function onLoadManagersFormData(state, { payload }) {
  const { data } = payload;

  const administrators = data.map(admin => {
    return {
      id: admin.id,
      name: `${admin.first_name} ${admin.last_name}`,
    };
  });

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      administrators,
    },
  };
}

function onFilterUnitsSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    filteredUnits: data || [],
  };
}

function onSetSelectedUnit(state, { payload }) {
  const { unit } = payload;

  return {
    ...state,
    selectedUnit: unit,
    filteredUnits: unit ? [unit] : [],
  };
}

export { validate };
export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.EDIT]: onEdit,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CANCEL]: onCancel,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.CREATE]: onCreate,
  [actions.TOUCH]: onTouch,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.LOAD_MANAGERS_FORM_DATA]: onLoadManagersFormData,
  [actions.FILTER_UNITS_SUCCESS]: onFilterUnitsSuccess,
  [actions.SET_SELECTED_UNIT]: onSetSelectedUnit,
});
